import { useCallback, useEffect, useRef } from 'react'

interface UseInfiniteScrollFetchProps {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  root?: HTMLElement | null
  rootMargin?: string
  threshold?: number
}
const useInfiniteScrollFetch = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  root = null,
  rootMargin = '50px',
  threshold = 0.1,
}: UseInfiniteScrollFetchProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const setObserverRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }

      if (!node || !hasNextPage || isFetchingNextPage) {
        return
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
          }
        },
        {
          root,
          rootMargin,
          threshold,
        },
      )

      observerRef.current.observe(node)
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage, root, rootMargin, threshold],
  )

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect()
    }
  }, [])

  return setObserverRef
}

export { useInfiniteScrollFetch }
